/**
 * RenderingScreen — routes between the 5 mode panels based on
 * controller.effectiveMode. Owns persistent per-root renderers
 * (SessionLiveRenderer, OrgShowcaseRenderer, AnnouncementsOnlyRenderer)
 * so their internal timers + trackers survive across re-renders.
 */

import { DeviceController } from '../state/controller.js';
import { AnnouncementsOnlyRenderer } from '../rendering/announcements-only.js';
import { renderIdle } from '../rendering/idle.js';
import { renderMaintenance } from '../rendering/maintenance.js';
import { OrgShowcaseRenderer } from '../rendering/org-showcase.js';
import { SessionLiveRenderer, deriveSessionDisplayPhase, renderSessionLifecycleOverlay } from '../rendering/session-live.js';
import { renderStudioScene } from '../rendering/studio-scene.js';
import { FullscreenControl } from '../rendering/fullscreen-control.js';
import { extractSignageScene } from '../scene-contract.js';

export class RenderingRouter {
  private session: SessionLiveRenderer | null = null;
  private showcase: OrgShowcaseRenderer | null = null;
  private announcements: AnnouncementsOnlyRenderer | null = null;
  private assignedIndex = 0;
  private assignedTimer: ReturnType<typeof setTimeout> | null = null;
  private assignedKey: string | null = null;
  private idleTipIndex = 0;
  private idleTipTimer: ReturnType<typeof setInterval> | null = null;
  private lastController: DeviceController | null = null;
  private dismissedEndedSessionKey: string | null = null;
  private readonly fullscreen: FullscreenControl;

  constructor(private readonly root: HTMLElement) {
    this.fullscreen = new FullscreenControl(root);
    this.idleTipTimer = setInterval(() => {
      this.idleTipIndex = (this.idleTipIndex + 1) % 999;
      // CHG-3265 (QA #282 sibling defect): the tip index is only visible
      // through renderIdle, so bumping it without a repaint never rotated
      // the on-screen tip. Repaint only while the idle panel is the active
      // surface — renderIdle stamps `ag-idle` on the root; other panels and
      // the unpaired/boot/upgrade screens own their own DOM + timers. Skip
      // while the diagnostic overlay is open: a cosmetic repaint clears
      // root.innerHTML and would destroy the overlay every 10 seconds.
      if (
        this.lastController !== null &&
        this.root.classList.contains('ag-idle') &&
        this.root.querySelector('.ag-diag') === null
      ) {
        this.render(this.lastController);
      }
    }, 10_000);
  }

  dispose(): void {
    if (this.idleTipTimer !== null) clearInterval(this.idleTipTimer);
    this.idleTipTimer = null;
    this.lastController = null;
    this.fullscreen.dispose();
    this.session?.dispose();
    this.showcase?.dispose();
    this.announcements?.dispose();
    this.session = null;
    this.showcase = null;
    this.announcements = null;
    this.stopAssignedRotation();
  }

  render(controller: DeviceController): void {
    this.lastController = controller;
    const assigned = assignedContent(controller.config);
    if (assigned) {
      this.renderAssigned(controller, assigned);
      return;
    }
    this.stopAssignedRotation();

    const mode = controller.effectiveMode;

    // Dispose renderers for modes we're leaving so their timers stop.
    if (mode !== 'session_live' && this.session) {
      this.session.dispose();
      this.session = null;
    }
    if (mode !== 'org_showcase' && this.showcase) {
      this.showcase.dispose();
      this.showcase = null;
    }
    if (mode !== 'announcements_only' && this.announcements) {
      this.announcements.dispose();
      this.announcements = null;
    }

    switch (mode) {
      case 'session_live': {
        if (!this.session) this.session = new SessionLiveRenderer(this.root);
        this.session.render({
          config: controller.config,
          sessionContent: controller.sessionContent,
        });
        return;
      }
      case 'org_showcase': {
        if (!this.showcase) this.showcase = new OrgShowcaseRenderer(this.root);
        this.showcase.render({
          config: controller.config,
          orgContent: controller.orgContent,
        });
        return;
      }
      case 'announcements_only': {
        if (!this.announcements)
          this.announcements = new AnnouncementsOnlyRenderer(this.root);
        this.announcements.render({
          config: controller.config,
          orgContent: controller.orgContent,
        });
        return;
      }
      case 'maintenance':
        renderMaintenance(this.root, controller.config);
        return;
      case 'idle':
      default:
        renderIdle(this.root, controller.config, controller.orgContent, this.idleTipIndex);
        return;
    }
  }

  private renderAssigned(controller: DeviceController, assigned: AssignedContent): void {
    let payload = assigned.payload;
    if (assigned.kind === 'playlist') {
      const items = assigned.items;
      if (items.length === 0) {
        renderIdle(this.root, controller.config, controller.orgContent, this.idleTipIndex);
        return;
      }
      if (this.assignedKey !== assigned.key) {
        this.assignedIndex = 0;
        this.assignedKey = assigned.key;
      }
      const item = items[this.assignedIndex % items.length]!;
      payload = item.payload;
      this.scheduleAssignedRotation(
        assigned.key,
        Math.max(5, item.durationSeconds),
        items.length,
        () => this.render(controller)
      );
    } else {
      this.stopAssignedRotation(assigned.key);
    }

    const scene = extractSignageScene(payload);
    if (!scene) {
      renderIdle(this.root, controller.config, controller.orgContent, this.idleTipIndex);
      return;
    }

    this.root.dataset.assignedContent = assigned.kind;
    this.root.dataset.assignedSceneId = scene.scene_id;
    this.renderScenePayload(controller, payload, scene.mode);
  }

  private renderScenePayload(
    controller: DeviceController,
    payload: Record<string, unknown>,
    mode: string
  ): void {
    if (mode !== 'session_live' && this.session) {
      this.session.dispose();
      this.session = null;
    }
    if (mode !== 'org_showcase' && this.showcase) {
      this.showcase.dispose();
      this.showcase = null;
    }
    if (mode !== 'announcements_only' && this.announcements) {
      this.announcements.dispose();
      this.announcements = null;
    }

    if (
      renderStudioScene(this.root, {
        config: controller.config,
        payload,
        sessionContent: controller.sessionContent,
        orgContent: controller.orgContent,
      })
    ) {
      const content = controller.sessionContent;
      const phase = content ? deriveSessionDisplayPhase(content) : 'live';
      const sessionKey = typeof content?.['session_id'] === 'string'
        ? content['session_id'] as string
        : typeof content?.['session_name'] === 'string'
          ? content['session_name'] as string
          : 'session';
      if (phase !== 'ended' && this.dismissedEndedSessionKey === sessionKey) {
        this.dismissedEndedSessionKey = null;
      }
      renderSessionLifecycleOverlay(this.root, content, controller.config, {
        dismissed: phase === 'ended' && this.dismissedEndedSessionKey === sessionKey,
        onDismiss: () => { this.dismissedEndedSessionKey = sessionKey; },
      });
      this.fullscreen.render();
      return;
    }

    switch (mode) {
      case 'session_live': {
        if (!this.session) this.session = new SessionLiveRenderer(this.root);
        this.session.render({
          config: controller.config,
          sessionContent: nonEmptyRecord(controller.sessionContent)
            ? controller.sessionContent
            : sessionContentFromScene(payload),
        });
        return;
      }
      case 'org_showcase': {
        if (!this.showcase) this.showcase = new OrgShowcaseRenderer(this.root);
        this.showcase.render({
          config: controller.config,
          orgContent: nonEmptyRecord(controller.orgContent)
            ? controller.orgContent
            : orgContentFromScene(payload),
        });
        return;
      }
      case 'announcements_only': {
        if (!this.announcements)
          this.announcements = new AnnouncementsOnlyRenderer(this.root);
        this.announcements.render({
          config: controller.config,
          orgContent: nonEmptyRecord(controller.orgContent)
            ? controller.orgContent
            : orgContentFromScene(payload),
        });
        return;
      }
      case 'maintenance':
        renderMaintenance(this.root, controller.config);
        return;
      case 'idle':
      default:
        renderIdle(this.root, controller.config, controller.orgContent, this.idleTipIndex);
        return;
    }
  }

  private scheduleAssignedRotation(
    key: string,
    durationSeconds: number,
    itemCount: number,
    onTick: () => void
  ): void {
    if (itemCount <= 1) {
      this.stopAssignedRotation(key);
      return;
    }
    if (this.assignedTimer !== null && this.assignedKey === key) return;
    this.stopAssignedRotation(key);
    this.assignedKey = key;
    this.assignedTimer = setTimeout(() => {
      this.assignedTimer = null;
      this.assignedIndex = (this.assignedIndex + 1) % itemCount;
      onTick();
    }, durationSeconds * 1000);
  }

  private stopAssignedRotation(nextKey: string | null = null): void {
    if (this.assignedTimer !== null) clearTimeout(this.assignedTimer);
    this.assignedTimer = null;
    this.assignedKey = nextKey;
  }
}

interface AssignedSceneItem {
  readonly payload: Record<string, unknown>;
  readonly durationSeconds: number;
}

type AssignedContent =
  | { readonly kind: 'scene'; readonly key: string; readonly payload: Record<string, unknown> }
  | {
      readonly kind: 'playlist';
      readonly key: string;
      readonly payload: Record<string, unknown>;
      readonly items: readonly AssignedSceneItem[];
    };

function assignedContent(config: Record<string, unknown> | null): AssignedContent | null {
  const payload = record(config?.['current_assignment_payload']);
  if (!payload) return null;
  if (extractSignageScene(payload)) {
    return { kind: 'scene', key: sceneKey(payload), payload };
  }
  const playlist = record(payload['playlist']);
  const rawItems = Array.isArray(playlist?.['items']) ? playlist['items'] : [];
  const items = rawItems
    .map((item) => {
      const itemRecord = record(item);
      const itemPayload = record(itemRecord?.['payload']);
      if (!itemRecord || !itemPayload || !extractSignageScene(itemPayload)) return null;
      return {
        payload: itemPayload,
        durationSeconds:
          typeof itemRecord['duration_seconds'] === 'number'
            ? Math.max(5, Math.floor(itemRecord['duration_seconds']))
            : 15,
      };
    })
    .filter((item): item is AssignedSceneItem => item !== null);
  if (playlist && items.length > 0) {
    const id = typeof playlist['id'] === 'string' ? playlist['id'] : 'playlist';
    const revisionKey = items.map((item) => sceneKey(item.payload)).join('|');
    return { kind: 'playlist', key: `${id}:${revisionKey}`, payload, items };
  }
  return null;
}

function sceneKey(payload: Record<string, unknown>): string {
  const scene = extractSignageScene(payload);
  return `${scene?.scene_id ?? 'scene'}:${payload['scene_revision_id'] ?? ''}`;
}

function sessionContentFromScene(payload: Record<string, unknown>): Record<string, unknown> {
  const progress = record(sourceData(payload, 'session_progress')) ?? {};
  return {
    session_id: stringValue(progress['session_id']),
    session_name:
      stringValue(progress['session_name']) ??
      stringValue(progress['name']) ??
      'Fundraising',
    goal_progress_minor_units:
      numberValue(progress['goal_progress_minor_units']) ??
      numberValue(progress['credited_total_minor_units']) ??
      numberValue(progress['total_raised_minor_units']) ??
      0,
    goal_minor_units: numberValue(progress['goal_minor_units']) ?? 0,
    celebration_thresholds: arrayValue(progress['celebration_thresholds']),
    feed: arrayValue(sourceData(payload, 'session_donation_feed')),
    pledge_feed: arrayValue(sourceData(payload, 'session_pledge_feed')),
    announcements: arrayValue(sourceData(payload, 'announcements')),
    milestones: arrayValue(sourceData(payload, 'milestones')),
    qr_live_url: record(sourceData(payload, 'qr_live_url')),
  };
}

function orgContentFromScene(payload: Record<string, unknown>): Record<string, unknown> {
  return {
    donor_wall: arrayValue(sourceData(payload, 'donor_wall')),
    top_campaign: record(sourceData(payload, 'top_campaign')),
    aggregate: record(sourceData(payload, 'aggregate')) ?? {},
    announcements: arrayValue(sourceData(payload, 'announcements')),
  };
}

function sourceData(payload: Record<string, unknown>, key: string): unknown {
  const scene = extractSignageScene(payload);
  const binding = scene?.source_bindings.find((source) => source.key === key);
  const raw = binding as unknown as Record<string, unknown> | undefined;
  const sample = record(raw?.['sample_data']);
  if (!sample) return null;
  if (Array.isArray(sample['items'])) return sample['items'];
  if ('value' in sample) return sample['value'];
  return sample;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyRecord(value: Record<string, unknown> | null): value is Record<string, unknown> {
  return Boolean(value && Object.keys(value).length > 0);
}

function arrayValue(value: unknown): readonly Record<string, unknown>[] {
  return Array.isArray(value) ? (value as readonly Record<string, unknown>[]) : [];
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}
